import { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { clsx } from 'clsx';
import logoImg from '../assets/logo.png';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      'hover:opacity-70 transition-opacity',
      isActive && 'border-b-2 border-current pb-1'
    );

  const navItems = [
    { to: '#', label: 'Visualizer' },
    { to: 'https://usecase.ge-room.com/', label: 'Customer Stories' },
    { to: '#', label: 'Pricing' },
    { to: '/a-propos', label: 'À propos' },
    { to: '#', label: 'Blog' },
  ];

  return (
    <nav className={clsx(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b",
      isScrolled
        ? "bg-white/90 backdrop-blur-md border-gray-200 text-black py-4"
        : "bg-transparent border-transparent text-white py-6"
    )}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Logo Icon */}
          <img src={logoImg} alt="GE-ROOM" className="w-8 h-8 object-contain" />
          <Link to="/" className="text-2xl font-sans font-bold tracking-tight">GE-ROOM</Link>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8 text-xs font-bold tracking-widest uppercase">
          {navItems.map((item) => (
            item.to.startsWith('/') ? (
              <NavLink
                key={item.label}
                to={item.to}
                className={navLinkClass}
              >
                {item.label}
              </NavLink>
            ) : item.to.startsWith('http') ? (
              <a
                key={item.label}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity"
              >
                {item.label}
              </a>
            ) : (
              <a
                key={item.label}
                href={item.to}
                className="hover:opacity-70 transition-opacity"
              >
                {item.label}
              </a>
            )
          ))}
          <a href="#" className="hover:opacity-70 transition-opacity">Français</a>
        </div>

        <div className="flex items-center gap-6">
          <a
            href="https://biz.ge-room.com/#/login?redirect=/products/productList"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:block px-6 py-3 bg-black text-white text-xs font-bold rounded-full hover:bg-gray-800 transition-colors uppercase tracking-wider"
          >
            Free Trial
          </a>
          <button
            className="md:hidden p-2 hover:bg-white/10 rounded-full transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={clsx(
          "md:hidden absolute top-full left-0 right-0 border-b",
          isScrolled ? "bg-white border-gray-200" : "bg-black/95 border-gray-800"
        )}>
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
            {navItems.map((item) => (
              item.to.startsWith('/') ? (
                <NavLink
                  key={item.label}
                  to={item.to}
                  className={({ isActive }) => clsx(
                    'text-sm font-bold tracking-wider uppercase py-2',
                    isActive && 'border-l-4 border-current pl-3'
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ) : item.to.startsWith('http') ? (
                <a
                  key={item.label}
                  href={item.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-bold tracking-wider uppercase py-2"
                >
                  {item.label}
                </a>
              ) : (
                <a
                  key={item.label}
                  href={item.to}
                  className="text-sm font-bold tracking-wider uppercase py-2"
                >
                  {item.label}
                </a>
              )
            ))}
            <a href="#" className="text-sm font-bold tracking-wider uppercase py-2">Français</a>
            <a
              href="https://biz.ge-room.com/#/login?redirect=/products/productList"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 px-6 py-3 bg-black text-white text-xs font-bold rounded-full hover:bg-gray-800 transition-colors uppercase tracking-wider text-center"
            >
              Free Trial
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};
